import BackButton from 'components/BackButton/BackButton';
import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { MAX_WIDTH_MOBILE } from 'utils/constant';
import { getLocalizedValue } from 'utils/helper';
import Button from '../../../components/Button';
import StepIndicator from '../../../components/StepIndicator';
import { CareProvider, Department, ServiceItem } from '../../../entities/Form';
import { FormValuesV2 } from '../../../entities/FormV2';
import styles from './Preview.module.scss';

interface FormPropsV2 {
  departmentOptions?: Department[];
  careProviderOptions?: CareProvider[];
  serviceItemOptions?: ServiceItem[];
  bookingTimeLabel: string;
  departmentDisplayName?: string;
  careProviderDisplayName?: string;
  serviceItemDisplayName?: string;
  values: FormValuesV2;
  onSubmit: (onError: () => void) => void;
  onBack: () => void;
}

const PreviewV2: FC<PropsWithChildren<FormPropsV2>> = ({
  departmentOptions,
  careProviderOptions,
  serviceItemOptions,
  bookingTimeLabel,
  departmentDisplayName,
  careProviderDisplayName,
  serviceItemDisplayName,
  values,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const [creatingBooking, setCreatingBooking] = useState(false);

  const onConfirm = () => {
    setCreatingBooking(true);
    onSubmit(() => setCreatingBooking(false));
  };

  return (
    <div className={styles.container}>
      <StepIndicator value={2} total={3} />
      <span className={styles.title}>{t('previewData')}</span>
      <BackButton
        title={t('previousStep')}
        onClick={onBack}
        disabled={creatingBooking}
        style={isMobile ? { top: '-74px' } : {}}
      />
      <div className={styles.content}>
        <div className={styles.block}>
          <div className={styles.header}>{t('outpatientService')}</div>
          <div className={styles.detail}>
            {departmentDisplayName ||
              departmentOptions?.find((option) => option.value === values.departmentValue)?.name}
          </div>
          <div className={styles.header}>{t('doctor')}</div>
          <div className={styles.detail}>
            {careProviderDisplayName ||
              careProviderOptions?.find((option) => option.value === values.careProviderHid)?.name}
          </div>
          <div className={styles.header}>{t('serviceItem')}</div>
          <div className={styles.detail}>
            {serviceItemDisplayName ||
              getLocalizedValue(serviceItemOptions?.find((option) => option.value === values.serviceItemHid))}
          </div>
          <div className={styles.header}>{t('appointmentTimeSlot')}</div>
          <div className={styles.detail}>{bookingTimeLabel}</div>
          <div className={styles.header}>{t('patientInfo')}</div>
          <div className={styles.title}>{t('name')}</div>
          <div className={styles.detail}>{`${values.lastName} ${values.firstName}`}</div>
          <div className={styles.title}>{t('optionalHkId')}</div>
          <div className={styles.detail}>{values.idNo || '-'}</div>
          <div className={styles.title}>{t('mobileNumber')}</div>
          <div className={styles.detail}>{`(${values.countryCode}) ${values.mobileNo}`}</div>
          <div className={styles.title}>{t('email')}</div>
          <div className={styles.detail}>{values.email || '-'}</div>
          <div className={styles.title}>{t('eHealth')}</div>
          <div className={styles.detail}>{values.isJoinedEhealth === 'Y' ? t('enabled') : t('disabled')}</div>
          <div className={styles.header}>{t('additionalInfo')}</div>
          <div className={styles.title}>{t('remarks')}</div>
          <div className={styles.detail}>{values.remarks || '-'}</div>
        </div>
        <div className={styles.buttons}>
          <Button type="primary" onClick={onConfirm} loading={creatingBooking}>
            <span>{t('confirm')}</span>
          </Button>
          <Button type="secondary" onClick={onBack}>
            <span>{t('edit')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreviewV2;
